import React, { useEffect } from "react";
import * as d3 from "d3";
import { Category, DataPoint } from "fileParsing/types";
import { Typography, useTheme } from "@mui/material";
import { TimelineRangeType } from "@store/filters/types";
import { PlainTooltip } from "../../Components/Tooltip";
import { toReadableDate } from "@utils/format";

export type HistogramSettings = {
  width: number;
  height: number;
  padding: { left: number; top: number; right: number; bottom: number };
  bins: number;
  transitionDuration: number;
  xAxesHidden: boolean;
  xAxisTicks: number;
  yAxesHidden: boolean;
  yAxisTicks: number;
  enableBrushing: boolean;
  minMaxBound: undefined | TimelineRangeType;
  selectionBounds: undefined | TimelineRangeType;
};

export const HistogramSettingsDefaults: HistogramSettings = {
  width: 1024,
  height: 140,
  padding: { left: 40, top: 10, right: 30, bottom: 20 },
  bins: 50,
  transitionDuration: 300,
  xAxesHidden: false,
  xAxisTicks: 12,
  yAxesHidden: false,
  yAxisTicks: 4,
  enableBrushing: false,
  minMaxBound: undefined,
  selectionBounds: undefined,
};

type CategorizedBin = {
  values: { [key in Category]: number };
  x0: Date;
  x1: Date;
};

// Component-specific props.
type ComponentProps = {
  data: DataPoint[];
  settings: Partial<HistogramSettings>;
  onBrush?: (selection: [Date, Date] | null) => void;
};

function Histogram(props: ComponentProps) {
  const theme = useTheme();

  const xAxisElement = React.createRef<SVGGElement>();
  const yAxisElement = React.createRef<SVGGElement>();
  const brushElement = React.createRef<SVGGElement>();

  const settings = { ...HistogramSettingsDefaults, ...props.settings };

  const dataTimeDomain = d3.extent(props.data, (d) => d.date);
  const [totalXBounds, setTotalXBounds] = React.useState<[Date, Date]>([
    settings.minMaxBound?.[0] || dataTimeDomain[0] || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
    settings.minMaxBound?.[1] || dataTimeDomain[1] || new Date(Date.now()),
  ]);

  const chartWidth = settings.width - settings.padding.left - settings.padding.right;
  const chartHeight = settings.height - settings.padding.top - settings.padding.bottom;

  const [yScaleDomain, setYScaleDomain] = React.useState([0, chartHeight]);
  const [stackedBars, setStackedBars] = React.useState<d3.Series<CategorizedBin, string>[]>([]);

  // This state is only used for Tooltip. Do not use it for anything other!
  const [brushedArea, _setBrushedArea] = React.useState<undefined | TimelineRangeType>(undefined);
  const brushedAreaRef = React.useRef(brushedArea);
  const setBrushedArea = (data: undefined | TimelineRangeType) => {
    brushedAreaRef.current = data;
    _setBrushedArea(data);
  };
  const [tooltipPos, setTooltipPos] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [tooltipSite, setTooltipSite] = React.useState<"left" | "right" | "middle">("right");

  const xScale = React.useMemo(
    () => d3.scaleTime().domain(totalXBounds).range([0, chartWidth]),
    [totalXBounds, chartWidth],
  );

  const yScale = React.useMemo(
    () => d3.scaleLinear<number, number>().domain(yScaleDomain).range([chartHeight, 0]),
    [yScaleDomain, chartHeight],
  );

  const brush = d3.brushX();

  if (settings.enableBrushing) {
    brush
      .on("end", (e) => {
        setBrushedArea(undefined);
        onBrush(e);
      })
      .on("brush", (e) => {
        if (e.sourceEvent && e.sourceEvent.pageX && e.sourceEvent.pageY) {
          if (e.mode === "drag")
            setTooltipPos({
              x: e.sourceEvent.pageX - 90,
              y: document.getElementById("timelineBrush")?.getBoundingClientRect().bottom || 0,
            });
          else
            setTooltipPos({
              x: e.sourceEvent.pageX - 45,
              y: document.getElementById("timelineBrush")?.getBoundingClientRect().bottom || 0,
            });
        }
        const selection = convertSelectionToDate(e);

        if (e.mode === "drag") setTooltipSite("middle");
        else if (selection[0].getTime() === brushedAreaRef.current?.[0]?.getTime()) setTooltipSite("right");
        else setTooltipSite("left");

        setBrushedArea(selection);
      })
      .extent([
        [0, 0],
        [chartWidth, chartHeight],
      ]);
  }

  useEffect(() => {
    setTotalXBounds([
      settings.minMaxBound?.[0] || dataTimeDomain[0] || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
      settings.minMaxBound?.[1] || dataTimeDomain[1] || new Date(Date.now()),
    ]);
    generateData();
    attachBrushing();
    drawBrushing();
  }, [props.data.length, settings.width, settings.height, settings.minMaxBound, settings.selectionBounds]);

  useEffect(() => {
    drawAxis();
    generateData();
  }, [totalXBounds]);

  useEffect(() => {
    drawAxis();
  }, [yScaleDomain]);

  useEffect(() => {
    attachBrushing();
    drawBrushing();
  }, []);

  function generateData() {
    // define histogram generator
    const histogram = d3
      .bin<DataPoint, Date>()
      .domain(totalXBounds)
      .value((d) => d.date)
      .thresholds(xScale.ticks(settings.bins));

    // generate bins
    const bins = histogram(props.data);

    // count data points per category in each bin
    const binsCategorized: CategorizedBin[] = bins.map((b) => ({
      values: b.reduce(
        (acc: Record<Category, number>, val: DataPoint) => {
          return { ...acc, [val.category]: acc[val.category] + 1 };
        },
        {
          [Category.Account]: 0,
          [Category.Activity]: 0,
          [Category.Contacts]: 0,
          [Category.Likes]: 0,
          [Category.Location]: 0,
          [Category.Media]: 0,
          [Category.Messages]: 0,
          [Category.Other]: 0,
          [Category.PostsAndComments]: 0,
          [Category.Security]: 0,
        },
      ),
      x0: b.x0 || new Date(),
      x1: b.x1 || new Date(),
    }));

    const stack = d3
      .stack<CategorizedBin>()
      .keys(Object.values(Category))
      .value((d, key) => d.values[key as Category] || 0)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const stackedBars = stack(binsCategorized);

    setYScaleDomain([0, Math.max(...bins.map((b) => b.length))]);
    setStackedBars(stackedBars);
  }

  const convertSelectionToDate = (e: d3.D3BrushEvent<Date>): [Date, Date] => {
    const [x0, x1]: [number, number] = e.selection ? (e.selection as [number, number]) : [0, chartWidth];
    return [xScale.invert(x0), xScale.invert(x1)];
  };

  function drawAxis() {
    if (!settings.xAxesHidden && xAxisElement.current)
      d3.select(xAxisElement.current).call(d3.axisBottom(xScale).ticks(settings.xAxisTicks));

    if (!settings.yAxesHidden && yAxisElement.current)
      d3.select(yAxisElement.current).call(d3.axisLeft(yScale).ticks(settings.yAxisTicks));
  }

  const drawBrushing = () => {
    if (props.data.length === 0) return;

    if (settings.enableBrushing && brushElement.current) {
      const start = xScale(props.settings.selectionBounds?.[0] || totalXBounds[0]);
      const end = xScale(props.settings.selectionBounds?.[1] || totalXBounds[1]);

      d3.select(brushElement.current).call(brush.move, [start, end]);
    }
  };

  function attachBrushing() {
    if (props.data.length === 0) return;

    if (settings.enableBrushing && brushElement.current) {
      const brushElement2 = d3.select(brushElement.current);
      brushElement2.call(brush);

      brushElement2.selectAll(".selection").attr("fill-opacity", 0.5).attr("fill", "#eee").attr("stroke", "none");

      brushElement2.selectAll(".handle").style("fill", "#7acc92");
    }
  }

  function onBrush(e: d3.D3BrushEvent<Date>) {
    if (props.data.length === 0 || !e.sourceEvent) return;

    // callback
    if (props.onBrush) props.onBrush(convertSelectionToDate(e));
  }

  const tooltip = () => {
    if (tooltipSite === "left") return brushedArea?.[0] && toReadableDate(brushedArea[0]);
    else if (tooltipSite === "right") return brushedArea?.[1] && toReadableDate(brushedArea[1]);

    return (
      brushedArea?.[1] && brushedArea?.[0] && `${toReadableDate(brushedArea[0])} - ${toReadableDate(brushedArea[1])}`
    );
  };

  return (
    <>
      <svg width="100%" viewBox={`0 0 ${settings.width} ${settings.height}`}>
        <g transform={`translate(${[settings.padding.left, settings.padding.top]})`}>
          {!settings.xAxesHidden && (
            <g
              ref={xAxisElement}
              transform={`translate(${[0, settings.height - settings.padding.top - settings.padding.bottom]})`}
            />
          )}
          {!settings.yAxesHidden && <g ref={yAxisElement} />}
          {stackedBars.map((series, index) => (
            <g key={index} fill={theme.palette.categories[series.key as Category].main}>
              {series.map((rect, index) => (
                <rect
                  key={index}
                  x={xScale(rect.data.x0 || 0)}
                  y={yScale(rect[1])}
                  width={Math.max(xScale(rect.data.x1 || 0) - xScale(rect.data.x0 || 0), 0)}
                  height={Math.max(yScale(rect[0]) - yScale(rect[1]), 0)}
                />
              ))}
            </g>
          ))}

          {settings.enableBrushing && (
            <g
              ref={brushElement}
              className="brush"
              id={"timelineBrush"}
              onDoubleClick={(e) => {
                if (totalXBounds?.[0] && totalXBounds?.[1] && props.onBrush) {
                  const padding = (totalXBounds[1].getTime() - totalXBounds[0].getTime()) * 0.03;
                  // check whether user clicked on handle
                  const bounds = document.getElementById("timelineBrush")?.getBoundingClientRect();

                  if (bounds && props.settings.selectionBounds?.[0] && props.settings.selectionBounds?.[1]) {
                    const clickedDate = xScale.invert(e.pageX - bounds.x);

                    // Click on left handle
                    if (
                      props.settings.selectionBounds[0].getTime() - padding < clickedDate.getTime() &&
                      props.settings.selectionBounds[0].getTime() + padding > clickedDate.getTime()
                    )
                      props.onBrush([totalXBounds[0], props.settings.selectionBounds[1]]);
                    // Click on right handle
                    else if (
                      props.settings.selectionBounds[1].getTime() - padding < clickedDate.getTime() &&
                      props.settings.selectionBounds[1].getTime() + padding > clickedDate.getTime()
                    )
                      props.onBrush([props.settings.selectionBounds[0], totalXBounds[1]]);
                    // Click somewhere else
                    else props.onBrush(totalXBounds);
                  }
                }
              }}
            />
          )}
        </g>
      </svg>

      <PlainTooltip
        visible={brushedArea !== undefined}
        position={tooltipPos}
        settings={{
          tooltipWidth: tooltipSite === "middle" ? 180 : 90,
          margin: 0,
        }}
        sx={{
          border: tooltipSite === "middle" ? "solid 1px #eee" : "solid 1px #7acc92",
        }}
      >
        <Typography variant={"body2"} align={"center"}>
          {tooltip()}
        </Typography>
      </PlainTooltip>
    </>
  );
}

export default Histogram;
